/**
 * <testimonial-slider>
 * A lightweight, dependency-free carousel with dot navigation.
 * Uses native scroll-snap for the sliding behaviour and keeps the
 * active dot in sync with the leading visible slide.
 */
class TestimonialSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.dotsWrap = this.querySelector('[data-slider-dots]');

    if (!this.track || !this.dotsWrap) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-slider-slide]'));
    this.dots = Array.from(this.dotsWrap.querySelectorAll('[data-slider-dot]'));

    if (this.slides.length === 0 || this.dots.length === 0) return;

    this.groups = [];
    for (let i = 0; i < this.slides.length; i += 3) {
      this.groups.push(this.slides.slice(i, i + 3));
    }

    this.activeIndex = 0;
    this.isClickScrolling = false;

    this.bindDotClicks();
    this.bindEvents();
    this.setActiveDot(0);
  }

  bindDotClicks() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToGroup(index));
    });
  }

  bindEvents() {
    let ticking = false;

    this.track.addEventListener(
      'scroll',
      () => {
        if (this.isClickScrolling) return;

        if (!ticking) {
          window.requestAnimationFrame(() => {
            this.syncActiveDotWithScroll();
            ticking = false;
          });
          ticking = true;
        }
      },
      { passive: true }
    );

    window.addEventListener('resize', () => this.syncActiveDotWithScroll());
  }

  goToGroup(index) {
    const group = this.groups[index];
    const slide = group?.[0];
    if (!slide) return;

    this.isClickScrolling = true;
    this.setActiveDot(index);

    this.track.scrollTo({
      left: slide.offsetLeft - this.track.offsetLeft,
      behavior: 'smooth',
    });

    window.clearTimeout(this.scrollResetTimeout);
    this.scrollResetTimeout = window.setTimeout(() => {
      this.isClickScrolling = false;
    }, 500);
  }

  syncActiveDotWithScroll() {
    const scrollLeft = this.track.scrollLeft;
    let activeIndex = 0;

    this.slides.forEach((slide, index) => {
      if (slide.offsetLeft - this.track.offsetLeft - scrollLeft <= 8) {
        activeIndex = Math.floor(index / 3);
      }
    });

    this.setActiveDot(activeIndex);
  }

  setActiveDot(index) {
    if (!this.dots) return;

    this.activeIndex = index;

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }
}

customElements.define('testimonial-slider', TestimonialSlider);